/* eslint-disable max-lines */
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import './MilestoneReport.scss';
import { TableUtility } from '@utility/dynamic-table';
import MilestoneInitial from '../MilestoneMIS/MilestoneInitial';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Toast } from 'primereact/toast';

import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MILESTONE_REPORT } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const MILESTONE_REPORT_TABLE_HEIGHT = 'calc(100vh - 250px)';

const fetchBaseURL = () => {
  const targetService = serviceName.PROJECT_MS || serviceName.RESELLER_SERVICE;
  const serviceURL = appendServiceNameToURL(targetService);
  const servicePort = appendServicePortToURL(targetService);
  let baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (!baseUrl) {
    if (
      process.env.NODE_ENV === 'production'
      && typeof window !== 'undefined'
      && window.location
      && window.location.origin
    ) {
      baseUrl = window.location.origin.replace(/\/$/, '');
    } else {
      baseUrl = 'http://localhost';
    }
  }

  const endpoint = MILESTONE_REPORT || '/v1/milestone-report';

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${endpoint}`;
  }

  const hasPort = /:\d+$/.test(baseUrl);
  if (hasPort) {
    return `${baseUrl}${serviceURL}/api${endpoint}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${endpoint}`;
};

const baseURL = fetchBaseURL();

const NUMERIC_CELL_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const isManagerColumnHeader = (hdr) => {
  if (!hdr) return false;
  const text = (hdr.textContent || '').toLowerCase();
  return text.includes('manager');
};

// The generic column renderer used by the table-utility package ignores the
// `body`/`width` fields from the column config, so the ellipsis + hover title
// for this column has to be applied directly on the rendered DOM cells.
const applyManagerColumnEllipsis = (table, colIndex) => {
  const bodyCells = table.querySelectorAll(
    `.p-datatable-tbody tr td:nth-child(${colIndex}), tbody tr td:nth-child(${colIndex})`,
  );

  bodyCells.forEach((cell) => {
    cell.style.maxWidth = '220px';
    cell.style.overflow = 'hidden';
    cell.style.textOverflow = 'ellipsis';
    cell.style.whiteSpace = 'nowrap';

    const content = cell.querySelector('div') || cell;
    const fullText = (content.textContent || '').trim();
    content.style.overflow = 'hidden';
    content.style.textOverflow = 'ellipsis';
    content.style.whiteSpace = 'nowrap';

    if (fullText) {
      cell.setAttribute('title', fullText);
      content.setAttribute('title', fullText);
    }
  });
};

const applyMilestoneManagerColumnStyling = () => {
  const container = document.querySelector('.milestone-report-table-utility')
    || document.querySelector('.milestone-report');
  if (!container) return;

  const tables = container.querySelectorAll('table, .p-datatable-table');
  tables.forEach((table) => {
    const headerCells = table.querySelectorAll('.p-datatable-thead tr th, thead tr th');
    headerCells.forEach((hdr) => {
      if (!isManagerColumnHeader(hdr)) return;
      const colIndex = Array.from(hdr.parentElement.children).indexOf(hdr) + 1;
      applyManagerColumnEllipsis(table, colIndex);
    });
  });
};

// The table-utility package pins every `.row-frozen` row to `bottom: 0`, so when
// there are two consecutive frozen rows (Total row + Total Project count row)
// they overlap instead of stacking. Offset each row by the height of the frozen
// rows below it so both stay visible, pinned above one another.
const applyMilestoneFrozenRowStacking = () => {
  const container = document.querySelector('.milestone-report-table-utility')
    || document.querySelector('.milestone-report');
  if (!container) return;

  const tbodies = container.querySelectorAll('.p-datatable-tbody, tbody');
  tbodies.forEach((tbody) => {
    const frozenRows = Array.from(tbody.querySelectorAll(':scope > tr.row-frozen'));
    let cumulativeOffset = 0;
    for (let rowIndex = frozenRows.length - 1; rowIndex >= 0; rowIndex -= 1) {
      const row = frozenRows[rowIndex];
      row.style.bottom = `${cumulativeOffset}px`;
      cumulativeOffset += row.offsetHeight;
    }
  });
};

const formatNumericCellValue = (value) => {
  if (value === undefined || value === null || value === '') return value;
  const numericValue = typeof value === 'number'
    ? value
    : Number(String(value).replace(/,/g, '')
      .trim());
  if (!Number.isFinite(numericValue)) return value;
  return NUMERIC_CELL_FORMATTER.format(numericValue);
};

// Format every column the backend flagged as `numeric` (totals + dynamic
// due/replanned/invoice monthly columns from milestone-report/businessLogic.js).
const applyMilestoneFormatting = (tableConfiguration, tableData) => {
  const safeConfig = Array.isArray(tableConfiguration) ? tableConfiguration : [];
  const numericFields = safeConfig
    .filter((col) => Boolean(col?.numeric) || col?.align === 'right')
    .map((col) => col.field);

  if (!numericFields.length) return Array.isArray(tableData) ? tableData : [];

  const formatRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const updatedRow = { ...row };
    numericFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedRow, field)) {
        updatedRow[field] = formatNumericCellValue(updatedRow[field]);
      }
    });
    return updatedRow;
  };

  return (Array.isArray(tableData) ? tableData : []).map(formatRow);
};

const handleCellClick = (arg1, arg2) => {
  const fieldPath = arg1?.fieldPath || arg2?.field || arg2?.columnKey || arg1?.field;
  const rowData = arg1?.rowData || arg1;
  const isProjectNameCol = fieldPath === 'projectName' || fieldPath === 'project_name';
  const idValue = rowData?.projectId || rowData?.project_id;
  if (isProjectNameCol && idValue) {
    const targetUrl = `/delivery/project-details?projectId=${encodeURIComponent(String(idValue))}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

// Marks the projectName cell as clickable via rowConfiguration.onClick so the
// table-utility renders it as a link (underline) and fires handleCellClick,
// same mechanism used by Project Wise Expense Report's applyProjectNameLink.
const applyProjectNameLink = (tableData) => {
  if (!Array.isArray(tableData)) return tableData;

  return tableData.map((row) => {
    if (!row || typeof row !== 'object') return row;

    const isFooterRow = row._isFooterRow === true || row.isTotalRow === true;
    const existingConfig = row.rowConfiguration || {};
    const existingOnClick = Array.isArray(existingConfig.onClick) ? existingConfig.onClick : [];

    if (isFooterRow) {
      const filteredOnClick = existingOnClick.filter(
        (item) => item?.field !== 'projectName' && item?.field !== 'project_name',
      );
      if (filteredOnClick.length === existingOnClick.length) return row;
      return { ...row, rowConfiguration: { ...existingConfig, onClick: filteredOnClick } };
    }

    const fieldsToAdd = ['projectName', 'project_name'].filter(
      (field) => !existingOnClick.some((item) => item?.field === field),
    );
    if (fieldsToAdd.length === 0) return row;

    return {
      ...row,
      rowConfiguration: {
        ...existingConfig,
        onClick: [...existingOnClick, ...fieldsToAdd.map((field) => ({ field }))],
      },
    };
  });
};

const getDateValidationMessage = (target) => {
  const existingMsg = target?.requiredValidationMessage || target?.requiredMessage;
  if (existingMsg && existingMsg !== 'Please select the date type') {
    return existingMsg;
  }

  const itemField = String(target?.field || '').toLowerCase();
  const template = String(target?.template || '').toLowerCase();
  const isRange = template.includes('range')
    || itemField.includes('range')
    || Array.isArray(target?.rangePlaceholders);

  if (isRange) {
    return 'Please select the date range';
  }
  return 'Please select the milestone date type';
};

const applyDateFilterRequiredValidation = (tableConfiguration = []) => {
  if (!Array.isArray(tableConfiguration)) return [];

  return tableConfiguration.map((column) => {
    const colField = String(column?.field || '').toLowerCase();
    const isColDate = colField.includes('date')
      || column?.field === 'dateType'
      || column?.field === 'date_type';

    const updatedMenuItems = Array.isArray(column?.menuItems)
      ? column.menuItems.map((menuItem) => ({
        ...menuItem,
        items: Array.isArray(menuItem?.items)
          ? menuItem.items.map((item) => {
            if (item?.type !== 'filter') return item;
            const itemField = String(item?.field || '').toLowerCase();
            const isItemDate = itemField.includes('date')
              || item?.template === 'dateRange'
              || item?.template === 'monthWiseCalendar'
              || item?.field === 'dateType'
              || item?.field === 'date_type';

            if (isItemDate) {
              const msg = getDateValidationMessage(item);
              return {
                ...item,
                isMandatory: true,
                isRequired: true,
                requiredOnMainApply: true,
                requiredMessage: msg,
                requiredValidationMessage: msg,
                tableUtilityConfig: {
                  ...(item?.tableUtilityConfig || {}),
                  requiredOnMainApply: true,
                  requiredMessage: msg,
                  requiredValidationMessage: msg,
                },
              };
            }
            return item;
          })
          : menuItem?.items,
      }))
      : column?.menuItems;

    if (isColDate) {
      const colMsg = getDateValidationMessage(column);
      return {
        ...column,
        isMandatory: true,
        isRequired: true,
        requiredOnMainApply: true,
        requiredMessage: colMsg,
        requiredValidationMessage: colMsg,
        tableUtilityConfig: {
          ...(column?.tableUtilityConfig || {}),
          requiredOnMainApply: true,
          requiredMessage: colMsg,
          requiredValidationMessage: colMsg,
        },
        menuItems: updatedMenuItems,
      };
    }

    return {
      ...column,
      menuItems: updatedMenuItems,
    };
  });
};

const appendProjectCountRow = (tableData = [], meta = {}) => {
  if (!Array.isArray(tableData) || tableData.length === 0) return tableData;

  const hasProjectCountRow = tableData.some(
    (row) => String(row?.projectName || row?.project_name || '').startsWith('Total Project'),
  );
  if (hasProjectCountRow) return tableData;

  const projectDataRows = tableData.filter(
    (row) => row?._isFooterRow !== true
      && row?.isTotalRow !== true
      && row?.projectName !== 'Total'
      && row?.project_name !== 'Total',
  );

  const overallCount = meta?.overallTotal ?? meta?.total ?? projectDataRows.length;
  const projectCount = Number(overallCount);
  if (projectCount === 0 && projectDataRows.length === 0) return tableData;

  const countLabel = `Total Project : ${projectCount}`;

  const projectCountRow = {
    projectName: countLabel,
    project_name: countLabel,
    _isFooterRow: true,
    isTotalRow: true,
    isProjectCountRow: true,
    rowConfiguration: {
      isFrozen: true,
      template: [
        { field: 'projectName', fontWeight: '700' },
        { field: 'project_name', fontWeight: '700' },
      ],
    },
  };

  return [...tableData, projectCountRow];
};

const handleFilteredApiResponse = (response) => {
  if (!response) return;

  const rawTableConfiguration = Array.isArray(response.data?.tableConfiguration)
    ? response.data.tableConfiguration
    : (Array.isArray(response.data?.data?.tableConfiguration)
      ? response.data.data.tableConfiguration
      : []);

  // Column order, visibility (Project Id / Total are hidden), and numeric/align
  // flags are all set by milestone-report/businessLogic.js; only the date-filter
  // mandatory-field validation still needs to be layered on here.
  const orderedTableConfiguration = applyDateFilterRequiredValidation(rawTableConfiguration);

  const rawTableData = response.data?.tableData
    || response.data?.data?.tableData
    || [];
  const validTableData = Array.isArray(rawTableData) ? rawTableData : [];

  const backendMeta = response.data?.meta
    || response.data?.data?.meta
    || response.meta
    || {};

  const overallTotalCount = Number(
    backendMeta.total
    ?? response.data?.totalProjects
    ?? response.data?.data?.totalProjects
    ?? response.data?.total
    ?? response.data?.data?.total
    ?? response.meta?.total
    ?? 0,
  );

  const projectDataRows = validTableData.filter(
    (row) => row?._isFooterRow !== true
      && row?.isTotalRow !== true
      && row?.projectName !== 'Total'
      && row?.project_name !== 'Total',
  );

  const finalProjectCount = overallTotalCount > 0 ? overallTotalCount : projectDataRows.length;

  const metaForCount = {
    ...backendMeta,
    overallTotal: finalProjectCount,
  };

  const tableDataWithCount = appendProjectCountRow(validTableData, metaForCount);

  const formattedTableData = applyProjectNameLink(applyMilestoneFormatting(
    orderedTableConfiguration,
    tableDataWithCount,
  ));

  response.data = {
    ...response.data,
    tableConfiguration: orderedTableConfiguration,
    tableData: formattedTableData,
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: orderedTableConfiguration,
      tableData: formattedTableData,
    },
  };

  const nonFooterCount = formattedTableData.filter(
    (row) => row?._isFooterRow !== true && row?.isTotalRow !== true,
  ).length;

  response.meta = {
    ...(response.meta || {}),
    total: Number(backendMeta.total ?? response.meta?.total ?? nonFooterCount ?? 0),
    page: Number(backendMeta.page ?? response.meta?.page ?? 1),
    limit: Number(backendMeta.limit ?? response.meta?.limit ?? 10),
  };
};

const ToolBarHeading = () => (
  <div className="milestone-report-toolbar-heading">
    <span className="milestone-report-toolbar-heading-title">
      Milestone Report
    </span>
    <div className="milestone-report-toolbar-heading-right">
      <span className="milestone-report-toolbar-heading-note">
        <strong>Note:</strong> Data is Available for the months after Apr-2022
      </span>
    </div>
  </div>
);

const MilestoneReport = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState(MILESTONE_REPORT_TABLE_HEIGHT);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const toastRef = useRef(null);
  const userAppliedFiltersRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    document.body.classList.add('milestone-report-page');
    return () => document.body.classList.remove('milestone-report-page');
  }, []);

  useEffect(() => {
    const handleResize = () => setTableScrollHeight(MILESTONE_REPORT_TABLE_HEIGHT);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const applyFrozenRowStyling = () => {
      applyMilestoneManagerColumnStyling();
      applyMilestoneFrozenRowStacking();
    };
    const observer = new MutationObserver(applyFrozenRowStyling);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleMouseOver = (event) => {
      const targetCell = event.target.closest('td');
      if (!targetCell) return;
      const contentEl = targetCell.querySelector('div')
        || targetCell.querySelector('span')
        || targetCell;
      const cellText = (contentEl.textContent || '').trim();
      if (!cellText) return;

      const isTextTruncated = contentEl.scrollWidth > contentEl.clientWidth
        || targetCell.scrollWidth > targetCell.clientWidth;

      if (isTextTruncated && !targetCell.hasAttribute('title')) {
        targetCell.setAttribute('title', cellText);
        if (contentEl !== targetCell) {
          contentEl.setAttribute('title', cellText);
        }
      }
    };

    const targetContainer = containerRef.current
      || document.querySelector('.milestone-report-outer-layer');
    if (targetContainer) {
      targetContainer.addEventListener('mouseover', handleMouseOver);
    }

    return () => {
      if (targetContainer) {
        targetContainer.removeEventListener('mouseover', handleMouseOver);
      }
    };
  }, []);

  const handleFilterUpdate = useCallback(() => {
    userAppliedFiltersRef.current = true;
  }, []);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    const rawMessage = response?.data?.message || response?.message;
    const isSuccess = response?.data?.success !== false;
    const isValidationMessage = Boolean(
      rawMessage
      && (
        rawMessage === 'Please select the date type'
        || rawMessage === 'Please select the milestone date type'
        || rawMessage.toLowerCase().includes('please select')
        || rawMessage.toLowerCase().includes('date type')
        || rawMessage.toLowerCase().includes('date filter')
        || rawMessage.toLowerCase().includes('date range')
      ),
    );

    const isInitialLoad = isInitialLoadRef.current;
    isInitialLoadRef.current = false;

    let displayMessage = rawMessage;
    if (displayMessage === 'Please select the date type') {
      displayMessage = 'Please select the milestone date type';
    }

    if (isValidationMessage || !isSuccess) {
      if (displayMessage && (!isInitialLoad || userAppliedFiltersRef.current)) {
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Validation',
          detail: displayMessage,
          life: 3000,
        });
      }
      setHasAppliedFilters(false);
    } else {
      const rawTableData = response?.data?.tableData || response?.data?.data?.tableData || [];
      const validTableData = Array.isArray(rawTableData) ? rawTableData : [];
      setHasAppliedFilters(validTableData.length > 0);
    }

    handleFilteredApiResponse(response);
  }, []);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('Milestone Report'),
          sheetName: () => getExportFileNameWithDate('Milestone Report'),
        },
      },

      handler: {
        onCellClickHandler: handleCellClick,
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
        onFilterUpdate: handleFilterUpdate,
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: <ToolBarHeading />,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },

    tableSettings: {
      size: 'small',
      loaderSkeleton: { rows: 8, bodyCells: 2, columns: 7 },
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
      paginationConfig: { page: 1, limit: 10, rowsPerPageOptions: [10, 20, 50, 100] },
    },
  }), [tableScrollHeight, handleFilteredApiResponseWithState, handleFilterUpdate, userProfile]);

  const isInitialState = !hasAppliedFilters;
  const containerClassName = [
    'milestone-report-outer-layer',
    isInitialState ? 'milestone-report--initial' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={containerClassName}>
      <Toast ref={toastRef} />
      <div className="milestone-report">
        <div className="milestone-report-table-utility">
          <TableUtility configuration={configuration} />
        </div>
        {isInitialState && <MilestoneInitial />}
      </div>
    </div>
  );
};

export default MilestoneReport;
